/**
 * Subscription Settings Page
 * Manages paywall configuration, trial settings, features, analytics, and products
 */

import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { CreditCard, Settings, BarChart3, Package, Paintbrush } from 'lucide-react';
import { useI18n } from '@/i18n';

// Tab components
import { SubscriptionSettingsTab } from '@/features/subscription/components/SubscriptionSettingsTab';
import { SubscriptionAnalyticsTab } from '@/features/subscription/components/SubscriptionAnalyticsTab';
import { SubscriptionProductsTab } from '@/features/subscription/components/SubscriptionProductsTab';
import { PaywallConfigTab } from '@/features/subscription/components/PaywallConfigTab';

type TabType = 'settings' | 'analytics' | 'products' | 'paywall';

export function SubscriptionSettingsPage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const currentTab = (tab as TabType) || 'settings';

  const TABS: { id: TabType; label: string; icon: typeof Settings }[] = [
    { id: 'settings', label: t('subscriptionPage.tabs.settings'), icon: Settings },
    { id: 'analytics', label: t('subscriptionPage.tabs.analytics'), icon: BarChart3 },
    { id: 'products', label: t('subscriptionPage.tabs.products'), icon: Package },
    { id: 'paywall', label: '付费墙配置', icon: Paintbrush },
  ];

  const handleTabChange = (newTab: TabType) => {
    if (newTab === 'settings') {
      navigate('/subscription-settings');
    } else {
      navigate(`/subscription-settings/${newTab}`);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('subscriptionPage.pageTitle')}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('subscriptionPage.pageDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={clsx(
                'flex items-center gap-2 pb-3 border-b-2 transition-colors',
                currentTab === id
                  ? 'border-primary-600 text-primary-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {currentTab === 'settings' && <SubscriptionSettingsTab />}
      {currentTab === 'analytics' && <SubscriptionAnalyticsTab />}
      {currentTab === 'products' && <SubscriptionProductsTab />}
      {currentTab === 'paywall' && <PaywallConfigTab />}
    </div>
  );
}
